import { useLoginOrConnectWallet } from '@/app/auth/artblocks-auth-provider'
import { useModalStatus } from '@privy-io/react-auth'
import { useSelector, useStore } from '@xstate/store/react'
import { Session } from 'next-auth'
import { useSession } from 'next-auth/react'
import { useEffect } from 'react'
import { useAuthenticatedWalletClient } from './use-authenticated-wallet-client'
import { WalletClient } from 'viem'

const DEFAULT_STATE = {
  session: null,
  bidModalOpen: false,
  buyModalOpen: false,
  connectingBeforeOpeningBidModal: false,
  connectingBeforeOpeningBuyModal: false,
  authenticatedWalletClient: undefined as WalletClient | undefined
}

/**
 * Manages the state and interactions for secondary market buyer actions (Buy Now, Make Offer).
 *
 * This hook orchestrates the opening of Reservoir Kit's Buy and Bid modals.
 * It handles the prerequisite wallet connection flow:
 * - If the user clicks "Buy Now" or "Make Offer" without being connected,
 *   it triggers the Privy login modal (`loginOrConnectWallet`).
 * - Upon successful authentication, it seamlessly opens the originally intended modal (Buy or Bid).
 * - It also checks if the connected user is the token owner. If so, it resets the state,
 *   as owner-specific actions should be displayed instead.
 *
 * @param token - The `TokenDetailsFragment` containing token details, used to check ownership.
 * @returns An object containing boolean states for modal visibility (`bidModalOpen`, `buyModalOpen`)
 *          and functions to trigger opening/closing the modals (`openBidModal`, `openBuyModal`,
 *          `closeBidModal`, `closeBuyModal`).
 */
export function useSecondaryBuyerModalState({ token }: { token: { owner_address: string } }): {
  bidModalOpen: boolean
  buyModalOpen: boolean
  openBidModal: () => void
  openBuyModal: () => void
  closeBidModal: () => void
  closeBuyModal: () => void
} {
  const { data: session } = useSession()
  const { loginOrConnectWallet } = useLoginOrConnectWallet()
  const { isOpen: privyModalOpen } = useModalStatus()
  const authenticatedWalletClient = useAuthenticatedWalletClient()

  const store = useStore({
    context: {
      ...DEFAULT_STATE,
      session
    },
    on: {
      openBidModal: (context, event: { loginFn: () => void }) => {
        if (context.session && context.authenticatedWalletClient) {
          return { ...context, bidModalOpen: true }
        }

        event.loginFn?.()
        return { ...context, connectingBeforeOpeningBidModal: true }
      },
      openBuyModal: (context, event: { loginFn: () => void }) => {
        if (context.session && context.authenticatedWalletClient) {
          return { ...context, buyModalOpen: true }
        }

        event.loginFn?.()
        return { ...context, connectingBeforeOpeningBuyModal: true }
      },
      closeBidModal: (context) => {
        return { ...context, bidModalOpen: false }
      },
      closeBuyModal: (context) => {
        return { ...context, buyModalOpen: false }
      },
      sessionUpdate: (
        context,
        event: { session: Session | null; authenticatedWalletClient: WalletClient | undefined }
      ) => {
        // User did not connect when prompted, reset state
        if (!event.session?.address) {
          return {
            ...DEFAULT_STATE,
            session: event.session
          }
        }

        // User is the owner of the token, reset state (Secondary owner actions should take over)
        if (event.session.address.toLowerCase() === token.owner_address.toLowerCase()) {
          return {
            ...DEFAULT_STATE,
            session: event.session
          }
        }

        // Continue flow from where we left off before connecting
        return {
          ...DEFAULT_STATE,
          session: event.session,
          authenticatedWalletClient: event.authenticatedWalletClient,
          bidModalOpen: context.connectingBeforeOpeningBidModal,
          buyModalOpen: context.connectingBeforeOpeningBuyModal
        }
      },
      connectModalClose: (context) => {
        // If the modal is closed and the user is not connected
        // we don't want to open the connect modal after the user
        // connects automatically.
        if (!context.session) {
          return { ...context, connectingBeforeOpeningBuyModal: false }
        }
      }
    }
  })

  useEffect(() => {
    store.trigger.sessionUpdate({ session, authenticatedWalletClient })
  }, [session, authenticatedWalletClient, store])

  useEffect(() => {
    if (!privyModalOpen) {
      store.trigger.connectModalClose()
    }
  }, [privyModalOpen, store])

  const { bidModalOpen, buyModalOpen } = useSelector(store, (snapshot) => {
    return snapshot.context
  })

  return {
    bidModalOpen,
    buyModalOpen,
    openBidModal: () => store.trigger.openBidModal({ loginFn: loginOrConnectWallet }),
    openBuyModal: () => store.trigger.openBuyModal({ loginFn: loginOrConnectWallet }),
    closeBidModal: store.trigger.closeBidModal,
    closeBuyModal: store.trigger.closeBuyModal
  }
}
